<template>
  <div class="occupancy-product">
    <div class="occupancy-product__wrap">
      <span class="mr-1">Per</span>
      <occupancy-icons
        v-b-popover.hover.top="create_icons.tooltip"
        :occupancy="create_icons"
      />
      <vb-icon
        v-b-popover.hover.top="create_icons.tooltip"
        :name="'info'"
        :color="sass('gray-500')"
        :size="13"
        class="occupancy-product__icon occupancy-product__icon--info ml-1"
      />
    </div>
    <span
      v-if="create_icons.free_stay_message"
      class="occupancy-product__alert"
    >
      {{ create_icons.free_stay_message }}
    </span>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { subtract } from 'mathjs'
import OccupancyIcons from '@bookingv3/components/atoms/occupancy_icons'

export default {
  name: 'occupancy-product',

  components: {
    'occupancy-icons': OccupancyIcons,
  },

  data() {
    return {}
  },

  props: {
    id: String,
    maximum_occupancy: Object,
  },

  computed: {
    ...mapState({
      // stato del modulo livebkv3
      occupancy: state => state.livebkv3.selected_solution.occupancy
    }),

    /**
     * Calcola l'occupancy effettiva del prodotto confrontando i bambini della ricerca
     * con le fasce d'età accettate dal prodotto.
     *
     * Input:
     * - this.occupancy: array di camere dalla ricerca utente, es. [{adults: 2, children: [3, 7]}]
     * - this.maximum_occupancy: capacità del prodotto con fasce d'età bambini, es:
     *   { adults: 2, children: [{from_age: 0, to_age: 5, total: 2, free_stay: true}] }
     *
     * Output:
     * - adults: numero adulti supportati dal prodotto
     * - children: numero bambini della ricerca che rientrano nelle fasce d'età del prodotto
     * - children_free: numero bambini con soggiorno gratuito
     * - free_stay_message: es. "Soggiorno gratis per il bambino di 3 anni"
     * - tooltip: descrizione testuale, es. "capienza massima di questa soluzione: 2 adulti e 2 bambini di 3, 7 anni"
     */
    create_icons() {
      const {adults, children} = this.maximum_occupancy
      const matched_ages = []  // età dei bambini effettivamente matchati
      const free_ages = []     // età dei bambini con soggiorno gratuito

      // Verifica quali bambini della ricerca sono compatibili con le fasce d'età del prodotto
      if (children) {
        // Copia le fasce per non mutare i dati originali (total viene decrementato)
        const available_children = children.map(child => ({...child}))

        for (const pax of this.occupancy) {
          for (const age of pax.children) {
            // Trova una fascia d'età disponibile che accetti questo bambino
            const slot = available_children.find(
              child => age >= child.from_age && age <= child.to_age && child.total > 0
            )
            if (slot) {
              slot.total = subtract(slot.total, 1)  // decrementa disponibilità fascia
              matched_ages.push(age)                // traccia l'età matchata
              if (slot.free_stay) free_ages.push(age)
            }
          }
        }
      }

      // Helper per formattare età bambini con label singolare/plurale
      const format_ages = (ages, singular, plural) => {
        const sorted = [...ages].sort((a, b) => a - b)
        const label = ages.length > 1 ? plural : singular
        // "anno" solo se c'è un unico bambino di 1 anno, altrimenti sempre "anni"
        const ageLabel = ages.length === 1 && sorted[0] === 1 ? 'anno' : 'anni'
        // "3 anni" / "3 e 5 anni" / "3, 5 e 7 anni"
        const text = sorted.length === 1
          ? `${sorted[0]} ${ageLabel}`
          : `${sorted.slice(0, -1).join(', ')} e ${sorted[sorted.length - 1]} ${ageLabel}`
        return {label, text}
      }

      // Genera il tooltip descrittivo per l'occupancy
      const children_count = matched_ages.length
      let tooltip = `capienza massima di questa soluzione: ${adults} ${adults > 1 ? 'adulti' : 'adulto'}`

      if (children_count > 0) {
        const fmt = format_ages(matched_ages, 'bambino', 'bambini')
        tooltip += ` e ${children_count} ${fmt.label} di ${fmt.text}`
      }

      // Genera il messaggio per il soggiorno gratuito bambini
      let free_stay_message = ''
      if (free_ages.length > 0) {
        const fmt = format_ages(free_ages, 'il bambino', 'i bambini')
        free_stay_message = `Soggiorno gratis per ${fmt.label} di ${fmt.text}`
      }

      this.$emit('badge-tooltip', this.id, tooltip)

      return {
        adults,
        children: children_count,
        children_free: free_ages.length,
        free_stay_message,
        tooltip
      }
    }
  },

  methods: {},
}
</script>

<style lang="scss" scoped>

  .occupancy-product {
    $this_occupancy: &;

    &__wrap {
      display: flex;
      align-items: center;
    }

    &__alert {
      font-size: $font-size-sm;
      margin-bottom: 0;
    }
  }

</style>